import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Badge } from "@shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  ChevronLeft,
  ChevronRight,
  History,
  TrendingUp,
  TrendingDown,
  Package,
  Truck,
  UserPlus,
  Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getProductStockHistory,
} from '@features/products/services/productService';
import { format } from "date-fns";
import { TIME_INTERVALS } from "@shared/utils/constants";

const adjustmentTypeIcons = {
  manual: Settings,
  pickup: Truck,
  package: Package,
  correction: UserPlus,
};

const adjustmentTypeColors = {
  manual: "bg-chart-1/10 text-chart-1 border-chart-1/20",
  pickup: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  package: "bg-destructive/10 text-destructive border-destructive/20",
  correction: "bg-chart-5/10 text-chart-5 border-chart-5/20",
};

export function StockMovementHistory({ product, trigger }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    adjustmentType: "",
    startDate: "",
    endDate: "",
    limit: 10,
  });

  // Fetch stock history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ["stockHistory", product?._id, currentPage, filters],
    queryFn: () =>
      getProductStockHistory(product._id, {
        page: currentPage,
        ...filters,
      }),
    enabled: open && !!product?._id,
  });

  // Fetch stock summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ["stockSummary", product?._id],
    queryFn: () => getProductStockHistory(product._id),
    enabled: open && !!product?._id,
  });

  const handleFilterChange = (key, value) => {
    // For API calls, convert "all" to empty string for adjustment type filter
    const apiValue = key === "adjustmentType" && value === "all" ? "" : value;
    setFilters((prev) => ({ ...prev, [key]: apiValue }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const renderMovementBadge = (movement) => {
    const Icon = adjustmentTypeIcons[movement.adjustmentType] || Settings;
    const colorClass =
      adjustmentTypeColors[movement.adjustmentType] ||
      "bg-muted text-muted-foreground border-border";

    return (
      <Badge
        variant="secondary"
        className={`${colorClass} flex items-center gap-1`}
      >
        <Icon className="w-3 h-3" />
        {t(`products.adjustmentTypes.${movement.adjustmentType}`)}
      </Badge>
    );
  };

  const renderAdjustmentValue = (adjustment) => {
    const isPositive = adjustment > 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? "text-chart-2" : "text-destructive";

    return (
      <div className={`flex items-center gap-1 ${colorClass} font-medium`}>
        <Icon className="w-4 h-4" />
        {isPositive ? "+" : ""}
        {adjustment}
      </div>
    );
  };

  const renderSummaryCard = () => {
    if (summaryLoading || !summaryData?.success) {
      return <div className="h-32 bg-muted animate-pulse rounded-md" />;
    }

    const { data: summary } = summaryData;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.currentStock")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.currentStock}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.recentActivity")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-1">
              {summary.recentActivity}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("products.last7Days")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.totalMovements")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-2">
              {summary.byType.reduce(
                (sum, type) => sum + type.totalMovements,
                0
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.movementTypes")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-5">
              {summary.byType.length}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderMovementTypeAnalysis = () => {
    if (summaryLoading || !summaryData?.success) return null;

    const { data: summary } = summaryData;

    return (
      <div className="space-y-4">
        <h4 className="font-medium">{t("products.movementsByType")}</h4>
        <div className="space-y-3">
          {summary.byType.map((type) => {
            const Icon = adjustmentTypeIcons[type._id] || Settings;
            const percentage = (
              (type.totalMovements /
                summary.byType.reduce((sum, t) => sum + t.totalMovements, 0)) *
              100
            ).toFixed(1);

            return (
              <div
                key={type._id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">
                      {t(`products.adjustmentTypes.${type._id}`)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {type.totalMovements} {t("products.movements")} (
                      {percentage}%)
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium">
                    {type.totalAdjustment > 0 ? "+" : ""}
                    {type.totalAdjustment}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(type.lastMovement), "MMM dd, yyyy")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <History className="w-4 h-4 mr-2" />
            {t("products.movementHistory")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            {t("products.stockMovementHistory")}
          </DialogTitle>
          <DialogDescription>
            {t("products.stockMovementHistoryDescription", {
              product: product?.name,
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="history" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="history">
              {t("products.movementHistory")}
            </TabsTrigger>
            <TabsTrigger value="analytics">
              {t("products.analytics")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="space-y-4">
            {/* Summary Cards */}
            {renderSummaryCard()}

            {/* Filters */}
            <div className="flex flex-wrap gap-4 p-4 bg-muted rounded-lg">
              <div className="flex-1 min-w-[200px]">
                <Label>{t("products.adjustmentType")}</Label>
                <Select
                  value={filters.adjustmentType || "all"}
                  onValueChange={(value) =>
                    handleFilterChange("adjustmentType", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("products.allTypes")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("products.allTypes")}
                    </SelectItem>
                    <SelectItem value="manual">
                      {t("products.adjustmentTypes.manual")}
                    </SelectItem>
                    <SelectItem value="pickup">
                      {t("products.adjustmentTypes.pickup")}
                    </SelectItem>
                    <SelectItem value="package">
                      {t("products.adjustmentTypes.package")}
                    </SelectItem>
                    <SelectItem value="correction">
                      {t("products.adjustmentTypes.correction")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 min-w-[150px]">
                <Label>{t("products.startDate")}</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </div>

              <div className="flex-1 min-w-[150px]">
                <Label>{t("products.endDate")}</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </div>

              <div className="flex-1 min-w-[100px]">
                <Label>{t("products.itemsPerPage")}</Label>
                <Select
                  value={filters.limit.toString()}
                  onValueChange={(value) =>
                    handleFilterChange("limit", parseInt(value))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">{TIME_INTERVALS.PAGINATION_OPTIONS[0].label}</SelectItem>
                    <SelectItem value="20">{TIME_INTERVALS.PAGINATION_OPTIONS[1].label}</SelectItem>
                    <SelectItem value="50">{TIME_INTERVALS.PAGINATION_OPTIONS[2].label}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* History Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("products.date")}</TableHead>
                    <TableHead>{t("products.type")}</TableHead>
                    <TableHead>{t("products.previousStock")}</TableHead>
                    <TableHead>{t("products.adjustment")}</TableHead>
                    <TableHead>{t("products.newStock")}</TableHead>
                    <TableHead>{t("products.user")}</TableHead>
                    <TableHead>{t("products.reason")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyLoading ? (
                    Array(5)
                      .fill(0)
                      .map((_, i) => (
                        <TableRow key={i}>
                          {Array(7)
                            .fill(0)
                            .map((_, j) => (
                              <TableCell key={j}>
                                <div className="h-4 bg-muted animate-pulse rounded" />
                              </TableCell>
                            ))}
                        </TableRow>
                      ))
                  ) : historyData?.data?.length > 0 ? (
                    historyData.data.map((movement) => (
                      <TableRow key={movement._id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {format(
                                new Date(movement.createdAt),
                                "MMM dd, yyyy"
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(movement.createdAt), "HH:mm")}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{renderMovementBadge(movement)}</TableCell>
                        <TableCell className="font-medium">
                          {movement.previousStock}
                        </TableCell>
                        <TableCell>
                          {renderAdjustmentValue(movement.adjustment)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {movement.newStock}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {movement.user?.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {movement.user?.email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{movement.reason}</div>
                            {movement.notes && (
                              <div className="text-xs text-muted-foreground">
                                {movement.notes}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 text-muted-foreground"
                      >
                        {t("products.noMovementHistory")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {historyData?.pageInfo && historyData.pageInfo.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {t("products.showingResults", {
                    start: (currentPage - 1) * filters.limit + 1,
                    end: Math.min(
                      currentPage * filters.limit,
                      historyData.totalItems
                    ),
                    total: historyData.totalItems,
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!historyData.pageInfo.hasPrevPage}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t("common.previous")}
                  </Button>
                  <span className="text-sm">
                    {t("common.pageOf", {
                      current: currentPage,
                      total: historyData.pageInfo.totalPages,
                    })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!historyData.pageInfo.hasNextPage}
                  >
                    {t("common.next")}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            {renderSummaryCard()}
            {renderMovementTypeAnalysis()}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
